// ===========================================
// Student Progress Report Filters Component
// ===========================================
// Filter form for student progress reports

import { useState } from 'react';
import {
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Box,
  MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AppDayjsLocalizationProvider } from '../common/AppDayjsLocalizationProvider';
import { FilterList as FilterIcon, Close as ClearIcon } from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';

import type { StudentProgressFilters } from '../../api/reports.api';
import { useTerms, useInstruments, useProgramLevels } from '../../hooks/useAdmin';
import { useTeachers, useStudents } from '../../hooks/useUsers';

// ===========================================
// TYPES
// ===========================================

interface StudentProgressFiltersProps {
  filters: StudentProgressFilters;
  onFiltersChange: (filters: StudentProgressFilters) => void;
  onApply: () => void;
  onReset: () => void;
}

// ===========================================
// COMPONENT
// ===========================================

export default function StudentProgressFilters({
  filters,
  onFiltersChange,
  onApply,
  onReset,
}: StudentProgressFiltersProps) {
  // Local state for date pickers (using dayjs)
  const [startDate, setStartDate] = useState<Dayjs | null>(
    filters.startDate ? dayjs(filters.startDate) : null
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(
    filters.endDate ? dayjs(filters.endDate) : null
  );

  // Fetch filter options
  const { data: terms = [] } = useTerms();
  const { data: instruments = [] } = useInstruments();
  const { data: programLevels = [] } = useProgramLevels();
  const { data: teachers = [] } = useTeachers();
  const { data: students = [] } = useStudents();

  // Filter to active terms only
  const activeTerms = terms.filter((term: any) => term.isActive);

  const handleFieldChange = (field: keyof StudentProgressFilters, value: string | undefined) => {
    onFiltersChange({ ...filters, [field]: value || undefined });
  };

  const handleStartDateChange = (newValue: Dayjs | null) => {
    setStartDate(newValue);
    onFiltersChange({
      ...filters,
      startDate: newValue ? newValue.format('YYYY-MM-DD') : undefined,
    });
  };

  const handleEndDateChange = (newValue: Dayjs | null) => {
    setEndDate(newValue);
    onFiltersChange({
      ...filters,
      endDate: newValue ? newValue.format('YYYY-MM-DD') : undefined,
    });
  };

  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
    onReset();
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <AppDayjsLocalizationProvider>
          <Grid container spacing={2}>
            {/* Student Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Student"
                value={filters.studentId || ''}
                onChange={(e) => handleFieldChange('studentId', e.target.value)}
                size="small"
              >
                <MenuItem value="">All Students</MenuItem>
                {students.map((student: any) => (
                  <MenuItem key={student.id} value={student.id}>
                    {student.firstName} {student.lastName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Term Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Term"
                value={filters.termId || ''}
                onChange={(e) => handleFieldChange('termId', e.target.value)}
                size="small"
              >
                <MenuItem value="">All Terms</MenuItem>
                {activeTerms.map((term: any) => (
                  <MenuItem key={term.id} value={term.id}>
                    {term.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Teacher Filter (reports are school-wide: roles slice 1a) */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Teacher"
                value={filters.teacherId || ''}
                onChange={(e) => handleFieldChange('teacherId', e.target.value)}
                size="small"
              >
                <MenuItem value="">All Teachers</MenuItem>
                {teachers.map((teacher: any) => (
                  <MenuItem key={teacher.id} value={teacher.id}>
                    {teacher.firstName} {teacher.lastName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Instrument Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Instrument"
                value={filters.instrumentId || ''}
                onChange={(e) => handleFieldChange('instrumentId', e.target.value)}
                size="small"
              >
                <MenuItem value="">All Instruments</MenuItem>
                {instruments.map((instrument: any) => (
                  <MenuItem key={instrument.id} value={instrument.id}>
                    {instrument.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Program Level Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Program Level"
                value={filters.programLevelId || ''}
                onChange={(e) => handleFieldChange('programLevelId', e.target.value)}
                size="small"
              >
                <MenuItem value="">All Levels</MenuItem>
                {programLevels.map((level: any) => (
                  <MenuItem key={level.id} value={level.id}>
                    {level.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Start Date */}
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={handleStartDateChange}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                  },
                }}
              />
            </Grid>

            {/* End Date */}
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={handleEndDateChange}
                minDate={startDate || undefined}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                  },
                }}
              />
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', gap: 1, height: '100%', alignItems: 'center' }}>
                <Button
                  variant="contained"
                  startIcon={<FilterIcon />}
                  onClick={onApply}
                  fullWidth
                >
                  Apply Filters
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={handleReset}
                  fullWidth
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </AppDayjsLocalizationProvider>
      </CardContent>
    </Card>
  );
}
